import { InvitationHistory } from './invitation';
import { LastFailedAnalysisErrorCodes } from './analysis';

export enum AggregatorName {
  BUDGET_INSIGHT = 'BUDGET_INSIGHT',
  BRIDGET = 'BRIDGE',
  TINK = 'TINK',
  OTHER = 'OTHER',
}

export enum BankConnectionMode {
  REDIRECT = 'REDIRECT',
  API = 'API',
}

export interface AggregationDetails {
  aggregatorName?: AggregatorName;
  callbackUrl?: string;
  token?: string;
  mode?: BankConnectionMode;
  redirectUrl?: string;
  apiUrl?: string;
  userId?: string;
  clientId?: string;
}

export interface Identity {
  nationality?: string;
  firstName?: string;
  lastName?: string;
  birthName?: string;
  birthDate?: string;
  birthCity?: string;
  birthZipCode?: string;
  birthCountry?: string;
}

export interface Contact {
  email?: string;
  phoneNumber?: string;
  street?: string;
  additionalInformation?: string;
  city?: string;
  zipCode?: string;
  country?: string;
}

export enum MaritalStatus {
  COHABITING = 'COHABITING',
  DIVORCED = 'DIVORCED',
  MARRIED = 'MARRIED',
  SEPARATED = 'SEPARATED',
  SINGLE = 'SINGLE',
  WIDOWED = 'WIDOWED',
  CIVIL_PARTNERSHIP = 'CIVIL_PARTNERSHIP',
}

export const singleMaritalStatuses = [
  MaritalStatus.DIVORCED,
  MaritalStatus.SEPARATED,
  MaritalStatus.SINGLE,
  MaritalStatus.WIDOWED,
];

export enum ResidentialStatus {
  TENANT = 'TENANT',
  OWNER = 'OWNER',
  OWNER_WITH_MORTGAGE = 'OWNER_WITH_MORTGAGE',
  HOUSED_BY_EMPLOYER = 'HOUSED_BY_EMPLOYER',
  HOUSED_BY_FAMILY = 'HOUSED_BY_FAMILY',
  HOUSED_BY_FRIEND = 'HOUSED_BY_FRIEND',
  OTHER = 'OTHER',
}

export interface Household {
  maritalStatus?: MaritalStatus;
  numberOfDependentChildren?: number;
  numberOfOtherDependents?: number;
}

export interface PersonalDetails {
  identity?: Identity;
  contact?: Contact;
  household?: Household;
}

export enum State {
  NEW = 'NEW',
  VIEWED = 'VIEWED',
  ARCHIVED = 'ARCHIVED',
}

export interface StateHistoryEntry {
  createdAt: string;
  state: State;
  user: string;
}

export interface CashFlowConfig {
  isIncludedInRemainderToLive: boolean;
}

export interface OnboardingOffer {
  taeg: number;
  monthly: number;
  total: number;
}

export interface OnboardingIdentity {
  documentType?: string;
  verified?: boolean;
}

export interface Onboarding {
  preAccepted?: boolean;
  offer?: OnboardingOffer;
  identity?: OnboardingIdentity;
  insurance?: { choice: InsuranceChoice };
  simulation?: { projectType?: ProjectType };
  income?: IncomeData;
  signed?: boolean;
  personalInfo: PersonalInfoState;
}

interface PersonalInfoState {
  firstName: string;
  lastName: string;
  birthDate: string;
  birthCity: string;
  birthCountry: string;
  phoneNumber: string;
  email: string;
}
interface IncomeProfile {
  numero_fiscal?: string;
  prenom?: string;
  nom?: string;
  date_naissance?: string;
  lieu_naissance?: string;
  email?: string;
  telephone_portable?: string;
  telephone_fixe?: string;
  adresse?: string;
}

interface TaxSituation {
  taux_personnalise?: string;
  situation_famille?: string;
  personnes_a_charge?: number;
  personnes_a_charge_detail?: unknown[];
  acomptes_mensuels?: string;
  option_taux_foyer_active?: boolean;
  nom_conjoint?: string;
}

interface IncomeData {
  taxConnected?: boolean;
  annualIncome?: number;
  profile?: IncomeProfile;
  taxSituation?: TaxSituation;
  pdfUrl?: string;
}

export type InsuranceChoice = 'death_disability' | 'complete' | 'none';
export type ProjectType = 'projet' | 'travaux' | 'auto' | 'etudiant';

export interface Dashboard {
  analysesConfig?: Record<string, Record<string, CashFlowConfig>>;
  createdAt: string;
  currentState: State;
  customizedMonthlyAmount?: number;
  statesHistory: StateHistoryEntry[];
  numberOfViews: number;
  numberOfAnalyses: number;
  team?: string;
  updatedAt: string;
  newWithAnalysis: boolean;
  onboarding?: Onboarding;
  comments?: Comment[];
  invitationHistory?: InvitationHistory[];
}

export interface Comment {
  value: string;
  authorEmail: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Share {
  organizationId: string;
  projectId: string;
  sharedAt: string;
}

export interface Project {
  organizationId: string;
  projectId: string;
  projectName?: string;
}

export enum CustomerStorageType {
  DELETION = 'DELETION',
  PARTIAL_DELETION = 'PARTIAL_DELETION',
  DEMO = 'DEMO',
}

export type Storage =
  | { type: CustomerStorageType.DELETION; expiresAt: string }
  | { type: CustomerStorageType.PARTIAL_DELETION; partiallyExpiresAt: string }
  | { type: CustomerStorageType.DEMO };

export interface LastFailedAnalysis {
  date: string;
  url: string;
  type: LastFailedAnalysisErrorCodes;
}

export enum UserSegment {
  PDF_POC = 'PDF_POC',
}

export enum UserJourneyState {
  CUSTOMER_CREATED = 'CUSTOMER_CREATED',
  INVITATION_SENT = 'INVITATION_SENT',
  STARTED = 'STARTED',
  BANK_SELECTED = 'BANK_SELECTED',
  QR_CODE_SCANNED = 'QR_CODE_SCANNED',
  AGGREGATION_STARTED = 'AGGREGATION_STARTED',
  AGGREGATION_FINISHED_ERROR = 'AGGREGATION_FINISHED_ERROR',
  AGGREGATION_FINISHED_SUCCESS = 'AGGREGATION_FINISHED_SUCCESS',
  FINISHED_ERROR = 'FINISHED_ERROR',
  FINISHED_SUCCESS = 'FINISHED_SUCCESS',
  ANALYSIS_ERROR = 'ANALYSIS_ERROR',
  ANALYSIS_SUCCESS = 'ANALYSIS_SUCCESS',
  CONSENT_GIVEN = 'CONSENT_GIVEN',
}

export interface Reports {
  lastFailedAnalysis?: LastFailedAnalysis;
  userJourneyState: UserJourneyState;
}
export interface Customer {
  id: string;
  customIdentifier: string;
  aggregationDetails: AggregationDetails;
  personalDetails?: PersonalDetails;
  personalDetailsOfCoBorrower?: PersonalDetails;
  dashboard: Dashboard;
  shares?: Share[];
  owner?: Project;
  storage?: Storage;
  reports: Reports;
  userSegment?: UserSegment.PDF_POC;
  createdAt?: string;
}

export interface PatchCustomerPersonalDetailsDto {
  personalDetails: {
    identity?: {
      firstName?: string;
      lastName?: string;
    };
    contact?: {
      email?: string;
      phoneNumber?: string;
    };
  };
  personalDetailsOfCoBorrower: {
    identity?: {
      firstName?: string;
      lastName?: string;
    };
    contact?: {
      email?: string;
      phoneNumber?: string;
    };
  };
}
